import { useState } from 'react'
import { useMutation } from '@apollo/client'

import ConfirmLockPublishedTemplatesModal from './ConfirmLockPublishedTemplatesModal'
import useModal from '../../../../lib/hooks/useModal'
import useNotification from '../../../../lib/hooks/useNotification'
import { BULK_LOCK_ALL_STUDENT_PLANS_MUTATION } from '../../../../graphql/plans'
import { BulkLockAllStudentPlansMutation } from '../../../../generated/graphql'
import {
  LOCK_JOB_STORAGE_KEY,
  LOCK_JOB_STARTED_EVENT,
  BulkLockJobStartedEventDetail
} from '../../constants/bulkLockConstants'

type ConfirmLockPublishedTemplatesModalWrapperProps = {
  /** Determines visibility of modal */
  isOpen: boolean
}

/**
 * Wrapper component that handles the lock published templates mutation.
 * Stores the jobId in localStorage and dispatches an event when the job starts.
 */
const ConfirmLockPublishedTemplatesModalWrapper = ({
  isOpen
}: ConfirmLockPublishedTemplatesModalWrapperProps) => {
  const { hideModal } = useModal()
  const { showSuccessNotif, showErrorNotif } = useNotification()
  const [isLoading, setIsLoading] = useState(false)

  const [bulkLockAllStudentPlans] = useMutation<BulkLockAllStudentPlansMutation>(
    BULK_LOCK_ALL_STUDENT_PLANS_MUTATION,
    {
      onCompleted: (data) => {
        setIsLoading(false)
        hideModal()

        const jobId = data.bulkLockAllStudentPlans?.jobId
        const message = data.bulkLockAllStudentPlans?.message || 'Lock job started successfully'

        if (jobId) {
          // Store jobId in localStorage
          localStorage.setItem(LOCK_JOB_STORAGE_KEY, jobId)

          // Dispatch custom event to notify BulkActionsPage
          window.dispatchEvent(
            new CustomEvent<BulkLockJobStartedEventDetail>(LOCK_JOB_STARTED_EVENT, {
              detail: { jobId }
            })
          )

          showSuccessNotif('Bulk lock operation started.')
        } else {
          showSuccessNotif(message)
        }
      },
      onError: (error) => {
        setIsLoading(false)
        hideModal()
        showErrorNotif(`Error starting lock job: ${error.message}`, bulkLockAllStudentPlans)
      }
    }
  )

  const onConfirm = () => {
    setIsLoading(true)
    bulkLockAllStudentPlans({
      variables: {
        locked: true
      }
    })
  }

  return <ConfirmLockPublishedTemplatesModal isOpen={isOpen && !isLoading} onConfirm={onConfirm} />
}

export default ConfirmLockPublishedTemplatesModalWrapper
